import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { useSnackbar } from 'notistack';

import Modal from '../../../../components/Modal/Modal';
import { authSet } from '../../../../utils/request';
import { setCoOrders } from '../../../../actions/coilovers/orders/patchCoOrders';

const fieldClassName = 'w-full rounded border border-gray-300 bg-transparent p-[10px]';

const CoOrderLateCorrectionModal = ({ item, onClose }) => {
  const { _id: id } = item;
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [mpn, setMpn] = useState(item.order_item_mpn);
  const [reason, setReason] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const submitting = useRef(false);
  const valid = mpn.trim() && reason.trim() && mpn.trim() !== item.order_item_mpn;

  const save = async () => {
    if (!valid || submitting.current) return;
    if (!confirming) {
      setConfirming(true);
      return;
    }
    submitting.current = true;
    setLoading(true);
    setError('');
    try {
      const { data } = await authSet('POST', `/co/orders/${id}/late_correction`, {
        order_item_mpn: mpn.trim(),
        previous_order_item_mpn: item.order_item_mpn,
        previous_order_status: item.order_status,
        reason: reason.trim(),
      });
      dispatch(setCoOrders(data));
      enqueueSnackbar('Order composition corrected. Stock and price updated.', { variant: 'success' });
      onClose();
    } catch (failure) {
      setError(failure?.message || 'Correction failed. Refresh the order before trying again.');
    } finally {
      submitting.current = false;
      setLoading(false);
    }
  };

  return (
    <Modal
      visible
      title="Late correction"
      subTitle={`Order ${item.order_id || id} | ${item.order_status}`}
      toggleVisible={() => { if (!submitting.current) onClose(); }}
      onSave={save}
      canSave={!valid}
      loading={loading}
      saveText={confirming ? 'Apply correction' : 'Review correction'}
    >
      <div className="flex max-w-[600px] flex-col gap-[15px]">
        <p>Stock will be adjusted by the composition difference. The order price will be recalculated automatically.</p>
        <p className="break-words">{`Current composition: ${item.order_item_mpn}`}</p>
        <p>{`Current price: ${item.order_assembly_price ?? '—'}`}</p>
        <label className="flex flex-col gap-[5px]" htmlFor="late-correction-mpn">
          New composition
          <input
            id="late-correction-mpn"
            className={fieldClassName}
            value={mpn}
            onChange={(event) => { setMpn(event.target.value); setConfirming(false); }}
            maxLength={10000}
            disabled={loading}
          />
        </label>
        <label className="flex flex-col gap-[5px]" htmlFor="late-correction-reason">
          Reason (required)
          <textarea
            id="late-correction-reason"
            className={fieldClassName}
            value={reason}
            onChange={(event) => { setReason(event.target.value); setConfirming(false); }}
            maxLength={2000}
            rows={3}
            disabled={loading}
          />
        </label>
        {confirming && (
          <div role="status" className="rounded bg-warning/10 p-[10px]">
            <p className="break-words">{`Apply new composition: ${mpn.trim()}`}</p>
            <p className="break-words">{`Reason: ${reason.trim()}`}</p>
            <p>This will update warehouse stock and price, and record your correction in the journal.</p>
          </div>
        )}
        {error && <p role="alert" className="text-danger">{error}</p>}
      </div>
    </Modal>
  );
};

CoOrderLateCorrectionModal.propTypes = {
  item: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    order_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    order_status: PropTypes.string.isRequired,
    order_item_mpn: PropTypes.string.isRequired,
    order_assembly_price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  onClose: PropTypes.func.isRequired,
};

export default CoOrderLateCorrectionModal;
